import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchEmails } from "../services/emailService";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";

export default function StatsPage() {
  const { t } = useTranslation();

  const [emails, setEmails] = useState([]);
  const [view, setView] = useState("week");

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {

    try {

      const first = await fetchEmails(1, 100);

      const totalPages = first.totalPages || 1;

      let all = [...(first.items || [])];

      for (let p = 2; p <= totalPages; p++) {

        const res = await fetchEmails(p, 100);

        all = [...all, ...(res.items || [])];

      }

      setEmails(all);

    }

    catch (err) {

      console.error("Stats error:", err);

    }

  };

  /* KPI */

  const total = emails.length;

  const aiHandled = emails.filter(e => e.category).length;

  const humanHandled = total - aiHandled;

  const aiResolutionRate = total
    ? ((aiHandled / total) * 100).toFixed(1)
    : 0;

  /* AVG RESPONSE TIME */

  const avgResponseTime = (() => {

    const resolved = emails.filter(e => e.responseTime);

    if (!resolved.length) return 0;

    const sum = resolved.reduce((acc, e) => acc + e.responseTime, 0);

    return (sum / resolved.length).toFixed(1);

  })();

  /* SATISFACTION */

  const avgSatisfaction = (() => {

    const rated = emails.filter(e => e.satisfaction);

    if (!rated.length) return 0;

    const sum = rated.reduce((acc, e) => acc + e.satisfaction, 0);

    return (sum / rated.length).toFixed(1);

  })();

  /* CATEGORY DATA */

  const categoryMap = {};

  emails.forEach(e => {

    const cat = e.category || "Other";

    categoryMap[cat] = (categoryMap[cat] || 0) + 1;

  });

  const categoryData = Object.keys(categoryMap).map(k => ({

    name: k,

    value: categoryMap[k]

  }));

  /* INTERACTIONS OVER TIME */

  const buildChart = () => {

    const map = {};

    emails.forEach(e => {

      const date = new Date(e.date);

      let key;

      if (view === "week") {

        key = date.toLocaleDateString("en-CA");

      }

      if (view === "month") {

        key = date.toLocaleString("default", { month: "short" });

      }

      if (view === "year") {

        key = date.getFullYear();

      }

      map[key] = (map[key] || 0) + 1;

    });

    return Object.keys(map).map(k => ({

      name: k,

      value: map[k]

    }));

  };

  const chartData = buildChart();

  const COLORS = ["#7b1735", "#b93b5a", "#e07a5f", "#81b29a", "#3d405b"];

  return (

    <div className="dashboard">

      <h2 className="title">{t('stats.title')}</h2>

      {/* KPI */}

      <div className="kpi-row">

        <div className="kpi-card">

          <p>{t('stats.totalInteractions')}</p>

          <h3>{total}</h3>

        </div>

        <div className="kpi-card">

          <p>{t('stats.avgResponseTime')}</p>

          <h3>{avgResponseTime}h</h3>

        </div>

        <div className="kpi-card">

          <p>{t('stats.aiResolutionRate')}</p>

          <h3>{aiResolutionRate}%</h3>

        </div>

        <div className="kpi-card">

          <p>{t('stats.avgSatisfaction')}</p>

          <h3>{avgSatisfaction}★</h3>

        </div>

      </div>

      {/* SECOND ROW */}

      <div className="two-columns">

        {/* AI VS STAFF */}

        <div className="stat-card">

          <h3>{t('stats.aiVsStaff')}</h3>

          <div className="bar-row">

            <span>{t('stats.aiResolved')}</span>

            <div className="bar">

              <div

                className="bar-fill"

                style={{ width: `${total ? aiHandled / total * 100 : 0}%` }}

              ></div>

            </div>

          </div>

          <div className="bar-row">

            <span>{t('stats.staffResolved')}</span>

            <div className="bar">

              <div

                className="bar-fill"

                style={{ width: `${total ? humanHandled / total * 100 : 0}%` }}

              ></div>

            </div>

          </div>

        </div>

        {/* CATEGORIES */}

        <div className="stat-card">

          <h3>{t('stats.interactionCategories')}</h3>

          <ResponsiveContainer width="100%" height={250}>

            <PieChart>

              <Pie

                data={categoryData}

                dataKey="value"

                nameKey="name"

                outerRadius={80}

                label

              >

                {

                  categoryData.map((entry, index) => (

                    <Cell key={index} fill={COLORS[index % COLORS.length]} />

                  ))

                }

              </Pie>

              <Legend />

            </PieChart>

          </ResponsiveContainer>

        </div>

      </div>

      {/* INTERACTIONS OVER TIME */}

      <div className="stat-card">

        <div className="time-filter">

          <button onClick={() => setView("week")}>{t('stats.days7')}</button>

          <button onClick={() => setView("month")}>{t('stats.month')}</button>

          <button onClick={() => setView("year")}>{t('stats.year')}</button>

        </div>

        <h3>{t('stats.interactionsOverTime')}</h3>

        <ResponsiveContainer width="100%" height={300}>

          <BarChart data={chartData}>

            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="name" />

            <YAxis />

            <Tooltip />

            <Bar dataKey="value" fill="#7b1735" />

          </BarChart>

        </ResponsiveContainer>

      </div>

    </div>

  );
}
